import { motion } from 'framer-motion'
import type { RunSummary } from '../types'
import { formatProbability, formatTimeAgo, formatDuration } from '../utils/format'
import clsx from 'clsx'

interface SummaryCardProps {
  summary: RunSummary
}

export function SummaryCard({ summary }: SummaryCardProps) {
  return (
    <div className="border border-signal-blue/30 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-signal-blue/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-signal-blue">📝</span>
          <span className="font-mono text-sm text-star">AI Summary</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-star-muted font-mono">
          <span>{summary.model_used}</span>
          {summary.generation_time_ms && (
            <span>{formatDuration(summary.generation_time_ms)}</span>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-5">
        {/* Executive summary */}
        <div>
          <h4 className="data-label mb-2">Executive Summary</h4>
          <p className="text-sm text-star leading-relaxed">
            {summary.executive_summary}
          </p>
        </div>
        
        {/* Agent highlights */}
        {summary.agent_highlights && Object.keys(summary.agent_highlights).length > 0 && (
          <div>
            <h4 className="data-label mb-2">Agent Perspectives</h4>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(summary.agent_highlights).map(([agent, highlight]) => (
                <div key={agent} className="p-2 bg-void-50 rounded">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx(
                      'text-lg',
                      highlight.direction === '↑' && 'text-signal-green',
                      highlight.direction === '↓' && 'text-signal-red',
                      highlight.direction === '→' && 'text-star-muted'
                    )}>
                      {highlight.direction}
                    </span>
                    <span className="font-mono text-xs text-star-muted capitalize">
                      {agent}
                    </span>
                  </div>
                  <p className="text-xs text-star-muted line-clamp-2">
                    {highlight.summary}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Key evidence */}
        {summary.key_evidence && summary.key_evidence.length > 0 && (
          <div>
            <h4 className="data-label mb-2">Key Evidence</h4>
            <div className="space-y-2">
              {summary.key_evidence.map((ev, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3 p-2 bg-void-50 rounded"
                >
                  <span className={clsx(
                    'flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs',
                    ev.impact === 'bullish' && 'bg-signal-green/20 text-signal-green',
                    ev.impact === 'bearish' && 'bg-signal-red/20 text-signal-red',
                    ev.impact === 'neutral' && 'bg-star-dim/20 text-star-muted'
                  )}>
                    {ev.impact === 'bullish' ? '↑' : ev.impact === 'bearish' ? '↓' : '→'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-star">{ev.fact}</p>
                    <p className="text-xs text-star-dim truncate mt-0.5">
                      {ev.source}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        {/* Probability reasoning */}
        {summary.probability_reasoning && (
          <div>
            <h4 className="data-label mb-2">Probability Reasoning</h4>
            <p className="text-xs text-star-muted leading-relaxed">
              {summary.probability_reasoning}
            </p>
          </div>
        )}
        
        {/* Market context */}
        {summary.market_context && (
          <div className="p-3 bg-void-50 rounded-lg">
            <h4 className="data-label mb-3">Market Context</h4>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <span className="text-xs text-star-dim">Market</span>
                <div className="font-mono text-sm text-star">
                  {formatProbability(summary.market_context.market_price)}
                </div>
              </div>
              <div>
                <span className="text-xs text-star-dim">Our Price</span>
                <div className="font-mono text-sm text-star">
                  {formatProbability(summary.market_context.our_price)}
                </div>
              </div>
              <div>
                <span className="text-xs text-star-dim">Edge</span>
                <div className={clsx(
                  'font-mono text-sm font-semibold',
                  summary.market_context.edge_pct > 0 ? 'text-signal-green' : 'text-signal-red'
                )}>
                  {summary.market_context.edge_pct > 0 ? '+' : ''}{summary.market_context.edge_pct.toFixed(1)}%
                </div>
              </div>
            </div>
            {summary.market_context.edge_interpretation && (
              <p className="text-xs text-star-muted">
                {summary.market_context.edge_interpretation}
              </p>
            )}
          </div>
        )}
        
        {/* Risks and caveats */}
        {summary.risks_and_caveats && summary.risks_and_caveats.length > 0 && (
          <div>
            <h4 className="data-label mb-2 flex items-center gap-1">
              <span className="text-signal-amber">⚠</span> Risks & Caveats
            </h4>
            <ul className="space-y-1">
              {summary.risks_and_caveats.map((risk, i) => (
                <li key={i} className="text-xs text-star-muted flex items-start gap-2">
                  <span className="text-signal-amber flex-shrink-0">•</span>
                  {risk}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Footer */}
        <div className="pt-3 border-t border-star-dim/20 text-xs text-star-dim font-mono flex items-center justify-between">
          <span>Generated: {formatTimeAgo(summary.generated_at)}</span>
          <span>{summary.model_provider}/{summary.model_used}</span>
        </div>
      </div>
    </div>
  )
}


